import { RadioGroup } from '@common/components/atoms/RadioGroup';
import { Skeleton } from '@common/components/atoms/Skeleton';
import { Icon } from '@common/components/foundations/icons';
import { Typography } from '@common/components/foundations/Typography';
import { DatePicker } from '@common/components/molecules/DatePicker';
import { subscriptionStatus } from '@common/config/app-config';
import { useViewStore } from '@common/store/view';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button, Divider, Group } from '@mantine/core';
import { DemoHotelSwitcher } from '@pages/Client/Account/Subscription/components/DemoHotelSwitcher';
import {
  useAddFreeTrial,
  useSetHotelState
} from '@pages/Client/Account/Subscription/hooks/usePayment';
import { useHotelDetails } from '@pages/Client/hooks/useHotelDetails';
import { HotelState } from '@pages/Client/hooks/useSubscription';
import dayjs from 'dayjs';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as z from 'zod';

const schema = z
  .object({
    state: z.number(),
    free_trial_end: z.date()
  })
  .refine(
    (data) => {
      const currentDate = new Date();
      return data.free_trial_end >= currentDate;
    },
    {
      message: 'Free trial end date cannot be in the past',
      path: ['free_trial_end']
    }
  );

const PARTNER_SUBSCRIBE_OPTIONS = [
  {
    label: 'Subscribe to Partner',
    value: HotelState.SUBSCRIBED_PARTNER
  },
  {
    label: 'Unsubscribe ',
    value: HotelState.CANCELED_PARTNER
  },
  {
    label: 'Free Trial',
    value: HotelState.FREE_TRIAL
  }
];

export const PartnerSubscription = () => {
  const { t } = useTranslation();
  const {
    hotelDetails: hotel,
    query: { refetch, isLoading }
  } = useHotelDetails();
  const { setHotelState, isLoading: isPartnerStateLoading } = useSetHotelState();
  const { mutate: addFreeTrialTime, isLoading: isAddFreetrialLoading } = useAddFreeTrial(true);

  const { view } = useViewStore();
  const isAdmin = view === 'admin';
  const isDemoHotel = hotel?.state === HotelState.DEMO;
  const isUnsubscribe = hotel?.state === HotelState.CANCELED_PARTNER;

  const borderColor = subscriptionStatus[hotel?.state ?? 0].border;
  const { control, reset, handleSubmit } = useForm<z.infer<typeof schema>>({
    resolver: zodResolver(schema),
    defaultValues: {
      free_trial_end: dayjs(hotel?.free_trial_end).toDate()
    }
  });

  useEffect(() => {
    if (hotel && !isLoading) {
      reset({ state: hotel.state ? hotel.state : 0 });
    }
  }, [hotel, isLoading]);

  const onSubmit = async (data: z.infer<typeof schema>) => {
    const { state, free_trial_end } = data;
    const originalState = hotel?.state;
    const originalFreeTrialEnd = hotel?.free_trial_end
      ? dayjs(hotel.free_trial_end).toDate()
      : null;

    const isStateChanged = state !== originalState;
    const isFreeTrialEndChanged = originalFreeTrialEnd
      ? free_trial_end.getTime() !== originalFreeTrialEnd.getTime()
      : true;

    if (isStateChanged) {
      await setHotelState({ state });
    }

    if (isFreeTrialEndChanged) {
      await addFreeTrialTime({
        free_trial_end: dayjs(free_trial_end).tz().format('YYYY-MM-DD')
      });
    }

    if (isStateChanged || isFreeTrialEndChanged) {
      refetch();
    }
  };

  return (
    <div className={`my-2 rounded-sm border bg-white pb-1 ${borderColor}`}>
      <div className="p-6 py-10">
        {hotel?.state === HotelState.SUBSCRIBED_PARTNER ? (
          <div className="mb-4 flex flex-col gap-4">
            <Typography element="h5" variant="h5" color="darkGrey" className="text-h1-bold">
              {t('You are subscribed.')}
            </Typography>
            <Typography>
              {t('If you have any questions, please contact your representative ') + ' '}
              {hotel?.reseller ? hotel.reseller.name : ''}.
            </Typography>
          </div>
        ) : null}

        {hotel?.is_locked &&
        hotel?.state !== HotelState.SUBSCRIBED_PARTNER &&
        hotel?.state !== HotelState.CANCELED_PARTNER &&
        hotel?.state !== HotelState.DEMO ? (
          <div className="mb-4 flex flex-col gap-4">
            <Typography element="h5" variant="h5" color="darkGrey" className="text-h1-bold">
              {hotel?.state == HotelState.SUSPENDED || hotel?.state == HotelState.NOT_ACTIVE
                ? 'Oops!'
                : t('All Good Things Come to an End...')}
            </Typography>
            <div className="flex max-w-3xl flex-col gap-3">
              <Typography color="uiRed">
                {hotel?.state == HotelState.SUSPENDED || hotel?.state == HotelState.NOT_ACTIVE
                  ? t(
                      'It seems like there is a problem with your credit card. We tried 3 times but unfortunately it didn`t work. Please update your details if they are incorrect. And please call us if you are having any issues.'
                    )
                  : t(
                      "But this one doesn't have to. Just pop in your credit card details to continue to benefit from our great prices and your increased revenue. And if you choose the monthly payment option, you will have the flexibility to be able to cancel with maximum one month's notice. We're quietly confident that you won't want to though."
                    )}
              </Typography>
              <Typography>
                {hotel?.state == HotelState.SUSPENDED || hotel?.state == HotelState.NOT_ACTIVE
                  ? ' '
                  : t(
                      ' If not, best of luck in the future. And let us know if we can ever be of service'
                    )}
              </Typography>
            </div>
          </div>
        ) : null}
        {hotel?.state === HotelState.CANCELED_PARTNER ? (
          <div className="mb-4 flex flex-col gap-4">
            <Typography element="h5" variant="h5" color="darkGrey" className="text-h1-bold">
              {t('Your Subscription Has Expired')}
            </Typography>
            <Typography color="uiRed">
              {t(
                ' We`re sorry, but your subscription has expired. Please contact your representative at'
              )}{' '}
              {hotel?.reseller ? hotel.reseller.name : ''}.
            </Typography>
          </div>
        ) : null}

        <div className="min-h-36 flex justify-between ">
          {isAdmin ? (
            <>
              <Group>
                <DemoHotelSwitcher />
                <Divider size="sm" ml={20} orientation="vertical" />
                {!isLoading ? (
                  <div>
                    <Controller
                      name="state"
                      control={control}
                      defaultValue={hotel?.state}
                      render={({ field }) => (
                        <RadioGroup
                          optionsWrapperClassName="flex gap-6"
                          defaultSelectedOption={field.value}
                          value={field.value}
                          onChange={field.onChange}
                          options={PARTNER_SUBSCRIBE_OPTIONS}
                        />
                      )}
                    />
                  </div>
                ) : (
                  <Skeleton className="h-10 w-[500px] " />
                )}
              </Group>
              <div className="flex items-center ">
                {isDemoHotel || isUnsubscribe ? null : (
                  <div className="flex items-end gap-4">
                    <Controller
                      control={control}
                      name="free_trial_end"
                      render={({ field: { value, onChange }, formState: { errors } }) => (
                        <div className="flex flex-col">
                          <DatePicker
                            ISOWeek={hotel?.starts_monday}
                            selectedDate={value || dayjs().add(7, 'day').toDate()}
                            onDateChange={(date) => {
                              onChange(date);
                            }}
                            timezone={hotel?.timezone}
                            closeOnSelect
                          />
                          <div>
                            {errors && (
                              <div className="flex items-center gap-2 text-error">
                                {errors.free_trial_end ? (
                                  <Icon.WarningOutline className="w-4" />
                                ) : (
                                  ''
                                )}
                                <Typography element="span" color="error" variant="meta-2">
                                  {errors.free_trial_end
                                    ? errors?.free_trial_end?.message?.toString()
                                    : ''}
                                </Typography>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    />
                    <Button
                      size="large"
                      onClick={handleSubmit(onSubmit)}
                      loading={isAddFreetrialLoading || isPartnerStateLoading}>
                      {t('Save')}
                    </Button>
                  </div>
                )}
              </div>
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
};
